/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// core components
import {
  loadCurrencies,
  loadBankAccountTypes,
  loadBanks,
  loadBankAccounts,
  saveBankAccounts,
  loadPartnerBankAccountsSummary
} from "../../actionCreators";
import { getCurrencies, getBankAccountTypes, getBankAccounts, getPartnerBankAccountsSummary } from "../../selectors";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import BankAccountComponent from "./BankAccountComponent";
import { shape, required, items, minLength, maxLength, errorMessage, concatFailure } from "@totalsoft/pure-validations";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { useDirtyInfo } from "@totalsoft/change-tracking-react";
import { groupBy } from "../../../../../src/utils/functions";

const BankAccountContainer = ({
  currencies,
  bankAccountTypes,
  partnerId,
  bankAccounts,
  bankAccountsLoaded,
  actions,
  summaryLoaded,
  partnerBankAccountsSummary,
  setIsDirty,
  hasWritePermission
}) => {
  const [localBankAccounts, setLocalBankAccountList] = useState(bankAccounts);
  const [cardExpanded, setExpanded] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [validDefaultBankAccounts, setValidDefaultBankAccounts] = useState(true);

  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();
  const validatorBankSelection = items(
    shape({
      bankId: required,
      bankAccount:
        [
          minLength(24) |> errorMessage("Validations.Generic.IBANLength"),
          maxLength(24) |> errorMessage("Validations.Generic.IBANLength")
        ] |> concatFailure,
      bankAccountTypeId: required,
      currencyId: required
    })
  );
  const [validationBankSelection, validateBankSelection] = useDirtyFieldValidation(validatorBankSelection, dirtyInfo);

  const onExpand = expanded => {
    if (cardExpanded !== expanded) setExpanded(expanded);
  };

  const addBankAccount = () => {
    let bankAccountsList = [...localBankAccounts, {}];
    setLocalBankAccountList(bankAccountsList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const getDefaultBankAccounts = localBankAccounts => {
    return localBankAccounts.filter(bank => bank.defaultAccount === true);
  };

  const defaultBankAccountsAreValid = defaultBankAccounts => {
    const result = groupBy(defaultBankAccounts, function (item) {
      return [item.defaultAccount, item.currencyId];
    });
    return !result.some(function (bankGroup) {
      return bankGroup.length > 1;
    });
  };

  const saveBankAccounts = (partnerId, localBankAccounts) => {
    if (!hasWritePermission || !validateBankSelection(localBankAccounts)) return;

    const defaultBankAccounts = getDefaultBankAccounts(localBankAccounts);
    if (defaultBankAccounts.length > 1 && !defaultBankAccountsAreValid(defaultBankAccounts)) {
      setValidDefaultBankAccounts(false);
    } else {
      actions.saveBankAccounts(partnerId, localBankAccounts, () => {
        setIsDirty(false);
      });
      setSaveDisabled(true);
    }
  };

  useEffect(() => {
    validateBankSelection(localBankAccounts, dirtyInfo);
  }, [localBankAccounts, dirtyInfo]);

  const deleteBankAccount = idx => {
    let newBankAccountList = localBankAccounts.filter(x => localBankAccounts.indexOf(x) !== idx);
    setLocalBankAccountList(newBankAccountList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  useEffect(() => {
    if (cardExpanded && !bankAccountsLoaded) {
      actions.loadCurrencies();
      actions.loadBankAccountTypes();
      if (partnerId !== null) actions.loadBankAccounts(partnerId);
    }
  }, [cardExpanded]);

  useEffect(() => {
    if (bankAccountsLoaded) {
      setLocalBankAccountList(bankAccounts);
    }
  }, [bankAccountsLoaded]);

  useEffect(() => {
    if (partnerId !== null) actions.loadPartnerBankAccountsSummary(partnerId);
  }, []);

  useEffect(() => {
    if (!summaryLoaded && partnerId !== null) {
      actions.loadPartnerBankAccountsSummary(partnerId);
    }
  }, [summaryLoaded]);

  const loadBanks = search => {
    if (!search) {
      return Promise.resolve([]);
    }

    return actions.loadBanks(search);
  };

  const onBankAccountPropertyChange = idx => (prop1, prop2) => (value1, value2) => {
    let newlocalBankAccounts = Object.assign([], localBankAccounts);
    setDirtyInfo(`${prop1}`);

    if (prop1 === "defaultAccount" && value1 === true) {
      let currentBankAccountCurrencyId = localBankAccounts[idx].currencyId;
      newlocalBankAccounts = localBankAccounts.map(el =>
        el.currencyId === currentBankAccountCurrencyId
          ? Object.assign({}, el, { [prop1]: false })
          : Object.assign({}, el)
      );
    }

    setLocalBankAccountList(
      Object.assign([], {
        ...newlocalBankAccounts,
        [idx]: {
          ...newlocalBankAccounts[idx],
          [prop1]: value1,
          ...(prop2 && { [prop2]: value2 })
        }
      })
    );
    setSaveDisabled(false);
    setValidDefaultBankAccounts(true);
    setIsDirty(true);
  };

  return (
    <BankAccountComponent
      currencies={currencies}
      bankAccountTypes={bankAccountTypes}
      bankAccounts={localBankAccounts}
      actions={actions}
      onPropertyChange={onBankAccountPropertyChange}
      loadBanks={loadBanks}
      onExpand={onExpand}
      addBankAccount={addBankAccount}
      partnerId={partnerId}
      localBankAccounts={localBankAccounts}
      saveBankAccounts={saveBankAccounts}
      deleteBankAccount={deleteBankAccount}
      cardExpanded={cardExpanded}
      saveDisabled={saveDisabled}
      validationBankSelection={validationBankSelection}
      partnerBankAccountsSummary={partnerBankAccountsSummary}
      validDefaultBankAccounts={validDefaultBankAccounts}
      hasWritePermission={hasWritePermission}
    />
  );
};
BankAccountContainer.propTypes = {
  currencies: PropTypes.array.isRequired,
  bankAccountTypes: PropTypes.array.isRequired,
  bankAccounts: PropTypes.array,
  bankAccountsLoaded: PropTypes.bool.isRequired,
  bankAccountsLoading: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  partnerId: PropTypes.number,
  summaryLoaded: PropTypes.bool,
  partnerBankAccountsSummary: PropTypes.object,
  setIsDirty: PropTypes.func,
  hasWritePermission: PropTypes.bool.isRequired
};

function mapStateToProps(state, ownProps) {
  const currencies = getCurrencies(state);
  const bankAccountTypes = getBankAccountTypes(state);
  const {
    content: bankAccounts,
    loaded: bankAccountsLoaded,
    loading: bankAccountsLoading
  } = getBankAccounts(state, ownProps.partnerId);
  const { partnerBankAccountsSummary, summaryLoaded } = getPartnerBankAccountsSummary(state, ownProps.partnerId);

  return {
    currencies,
    bankAccountTypes,
    bankAccounts,
    bankAccountsLoaded,
    bankAccountsLoading,
    partnerBankAccountsSummary,
    summaryLoaded
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadBankAccountTypes,
        loadCurrencies,
        loadBanks,
        loadBankAccounts,
        saveBankAccounts,
        loadPartnerBankAccountsSummary
      },
      dispatch
    ),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BankAccountContainer);
